import * as React from "react";
import {useEffect, useState} from "react";

interface DraftInputOptions<T> {
	/** The committed value owned by the parent. */
	value: T;
	/** Value → normalized display text. */
	format: (value: T) => string;
	/** Display text → value; the draft is re-formatted from its result on commit. */
	parse: (draft: string) => T;
	/** Fired with the parsed draft on blur (or Enter), only when its display text differs from the committed value's. */
	onValueChange: (value: T) => void;
	onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
}

/**
 * Local draft state for text inputs that commit a parsed value on blur (or Enter): the user types freely
 * without the parent reformatting on every keystroke, and the draft is normalized from the parsed value on
 * commit. While the input is focused, `value` changes from the parent are ignored so an in-progress draft
 * is never overwritten. Returns the value/handler props to spread onto the `<input>`.
 *
 * Change detection compares display texts rather than values, so `T` can be a non-primitive value (e.g. a
 * Temporal.ZonedDateTime) without an equality function — but it must then be reference-stable across renders
 * (memoized), or the reformat effect fires on every render.
 */
export function useDraftInput<T>({value, format, parse, onValueChange, onFocus}: DraftInputOptions<T>) {
	const [draft, setDraft] = useState(() => format(value));
	const [focused, setFocused] = useState(false);

	// `format` is deliberately not a dependency so callers may pass inline functions; only value/focus
	// changes should re-format the draft.
	useEffect(() => {
		if (!focused) {
			setDraft(format(value));
		}
	}, [focused, value]);

	return {
		value: draft,
		onFocus: (event: React.FocusEvent<HTMLInputElement>) => {
			setFocused(true);
			onFocus?.(event);
		},
		onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
			setDraft(event.target.value);
		},
		onBlur: () => {
			const nextValue = parse(draft);
			const nextDraft = format(nextValue);
			setFocused(false);
			setDraft(nextDraft);
			if (nextDraft !== format(value)) {
				onValueChange(nextValue);
			}
		},
		onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
			if (event.key === "Enter") {
				event.currentTarget.blur();
			}
		}
	};
}
